import { Injectable } from '@angular/core';
import { WizardModel } from '../wizard-model';
import { ConfigurationTemplate } from '@configuration/wizard/models/wizard-model.interface';
import { Log } from '@shared/debug/log';
import { OverlayService } from '@shared/overlay/overlay.service';
import { ConfigurationCreationService } from '../../service/configuration-creation.service';
import { RouteNavigationService } from '@services/route-navigation.service';
import { CreateProjectService } from '@project/service/create-project.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { AddArticleService } from '@configurator/project/add-article.service';
import { lastValueFrom } from 'rxjs';
import { OverlayBackground } from '@shared/overlay/overlay.type';

@Injectable()
export class TemplateModel extends WizardModel {
  public readonly type = 'TEMPLATE';

  private articleNo: string | undefined;

  public constructor(
    private overlayService: OverlayService,
    private configurationCreationService: ConfigurationCreationService,
    private routeNavigationService: RouteNavigationService,
    private createProjectService: CreateProjectService,
    private articleDataService: ArticleDataService,
    private addArticleService: AddArticleService,
  ) {
    super();
  }

  protected createConfig(): ConfigurationTemplate {
    return {
      id: 'create-from-template',
      type: 'type',
      // Note: Not using SvgIconName because it renders too small, using icon path instead.
      imageUrl: 'ng-assets/img/icons/applications.svg',
      label: 'configurationCreation.type.template',
      steps: [
        {
          type: 'meta',
          id: 'meta',
          label: 'configurationCreation.progress.configuration_data',
        },
        {
          type: 'templateSelection',
          id: 'templateSelection',
          label: 'configurationCreation.progress.configuration_data',
        },
      ],
    };
  }

  public selectValue(step: number, value: any): void {
    const currentStepConfig = this.config.steps[step];
    if (step < 0 || !currentStepConfig) {
      return;
    }
    this.articleNo = value;
  }

  public async create(): Promise<void> {
    const theArticleNo = this.articleNo;
    if (theArticleNo === undefined) {
      // In practice, this should never happen because the UI prevents proceeding without selection
      Log.error('No template article selected!');
      return;
    }
    if (this.creationStarted) {
      return;
    }
    this.creationStarted = true;

    const meta = this.metaData;
    try {
      await this.overlayService.doWithOverlay(async () => {
        await this.configurationCreationService.prepareNewConfiguration(meta);

        try {
          await this.routeNavigationService.toConfigurator();
        } catch (error) {
          Log.error('Could not navigate to configurator: ', error);
          return;
        }

        await this.createProjectService.createFreeMounting();
        const articles = await lastValueFrom(this.articleDataService.getLatestArticlesByNoObservable([theArticleNo]));
        await this.addArticleService.addArticlesToMountingRail(articles);
      }, OverlayBackground.color);
    } catch (error) {
      Log.error('Could not create configuraion: ', error);
    } finally {
      this.creationStarted = false;
    }
  }
}
